'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { HiOutlineSearch } from '@react-icons/all-files/hi/HiOutlineSearch';
import { HiOutlineDocumentReport } from '@react-icons/all-files/hi/HiOutlineDocumentReport';
import { HiOutlineBookOpen } from '@react-icons/all-files/hi/HiOutlineBookOpen';
import {
  StyledToolHero,
  StyledToolBody,
  StyledCalculator,
  StyledFieldsetTitle,
  StyledOutputCard,
  StyledCodeBlock,
  StyledHowTo,
  StyledWidgetsSection,
  StyledWidgetGrid,
  StyledWidgetCard,
  StyledRelatedTools,
  StyledRelatedToolGrid,
  StyledRelatedToolCard,
  StyledUrlForm,
  StyledFormError,
  StyledSpinner,
  StyledTypeChips,
  StyledReviewNote,
  StyledNotesBlock,
} from './AiSchemaTool.styles';

interface IWidget {
  _id: string;
  name: string;
  slug: string;
  logo: string;
  teaser: string;
}

interface IRelatedTool {
  slug: string;
  name: string;
  description: string;
}

interface IProps {
  recommendedWidgets: IWidget[];
  relatedTools: IRelatedTool[];
}

interface IGeneratedSchema {
  type: string;
  jsonld: Record<string, unknown>;
  note?: string;
}

interface IResult {
  url: string;
  pageTitle: string;
  detectedTypes: string[];
  schemas: IGeneratedSchema[];
  combined: string;
  notes: string;
}

const TOOL_ICONS: Record<string, React.ReactNode> = {
  'website-schema-generator': <HiOutlineSearch />,
  'article-schema-generator': <HiOutlineDocumentReport />,
  'product-schema-generator': <HiOutlineBookOpen />,
  'faq-schema-generator': <HiOutlineDocumentReport />,
  'event-schema-generator': <HiOutlineBookOpen />,
  'recipe-schema-generator': <HiOutlineDocumentReport />,
};

function getWidgetDescription(slug: string): string {
  switch (slug) {
    case 'accordion':
      return 'Turn dense pages into clean, collapsible sections so readers scan first and dig in where it matters.';
    case 'tabs':
      return 'Split content into tabs so visitors jump straight to what they came for. Clear structure backs up your schema.';
    case 'faq':
      return 'Add an on-page FAQ that pairs naturally with FAQ schema and answers buyer questions in one place.';
    case 'feeds':
      return 'Keep your site fresh with auto-updating content feeds, a signal that your site is active and maintained.';
    default:
      return 'Strengthen your pages with free CommonNinja widgets.';
  }
}

function getWidgetCTA(slug: string): string {
  switch (slug) {
    case 'accordion':
      return 'Add Accordion Free →';
    case 'tabs':
      return 'Add Tabs Free →';
    case 'faq':
      return 'Add FAQ Free →';
    case 'feeds':
      return 'Add Feeds Free →';
    default:
      return 'Try It Free →';
  }
}

export const AiSchemaTool = ({ recommendedWidgets, relatedTools }: IProps) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<IResult | null>(null);
  const [copiedKey, setCopiedKey] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim() || loading) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch('/api/generate-schema', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Something went wrong. Please try again.');
      } else {
        setResult(data as IResult);
      }
    } catch {
      setError('We could not reach the generator. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    window.setTimeout(() => setCopiedKey(''), 2000);
  };

  return (
    <>
      <StyledToolHero>
        <h1>Free <span className="highlight">AI Schema</span> Generator</h1>
        <p>
          Paste a page URL and let AI read it, work out what it is, and generate the right
          schema.org JSON-LD for you. Review, copy, and validate. Free, no sign-up required.
        </p>
      </StyledToolHero>

      <StyledToolBody>
        <StyledCalculator>
          <h2>Generate schema from a URL</h2>
          <StyledFieldsetTitle>
            Your page
            <span>Enter the public URL of the page you want to mark up. The AI reads that page only.</span>
          </StyledFieldsetTitle>
          <StyledUrlForm onSubmit={handleSubmit}>
            <input
              type="url"
              placeholder="https://www.example.com/your-page"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              aria-label="Page URL"
            />
            <button type="submit" disabled={loading || !url.trim()}>
              {loading ? 'Reading page...' : 'Generate Schema'}
            </button>
          </StyledUrlForm>
          {error && <StyledFormError>{error}</StyledFormError>}
        </StyledCalculator>

        {loading && (
          <StyledOutputCard>
            <StyledSpinner>
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
              <span>Reading the page and generating schema. This can take a few seconds.</span>
            </StyledSpinner>
          </StyledOutputCard>
        )}

        {result && (
          <StyledOutputCard>
            <p className="output-intro">
              Schema generated for <strong>{result.pageTitle || result.url}</strong>. This is an AI
              draft from the page content.
            </p>

            {result.detectedTypes.length > 0 && (
              <StyledTypeChips>
                {result.detectedTypes.map((t) => (
                  <span className="chip" key={t}>{t}</span>
                ))}
              </StyledTypeChips>
            )}

            {result.notes && (
              <StyledNotesBlock>
                <b>What we found: </b>{result.notes}
              </StyledNotesBlock>
            )}

            <StyledReviewNote>
              <strong>Review before you publish.</strong> AI reads what is on the page and can miss
              or misread content, especially anything loaded with JavaScript. Check every value,
              prices, ratings, dates, and names, against your real page, and fill in anything missing
              before you use this markup.
            </StyledReviewNote>

            <StyledCodeBlock>
              <div className="code-header">
                <h3>Combined JSON-LD</h3>
                <button className="copy-button" onClick={() => copy(result.combined, 'combined')}>
                  {copiedKey === 'combined' ? 'Copied!' : 'Copy to Clipboard'}
                </button>
              </div>
              <pre>{result.combined}</pre>
            </StyledCodeBlock>

            {result.schemas.length > 1 &&
              result.schemas.map((s, i) => {
                const block = `<script type="application/ld+json">\n${JSON.stringify(s.jsonld, null, 2)}\n</script>`;
                return (
                  <StyledCodeBlock key={`${s.type}-${i}`}>
                    <div className="code-header">
                      <h3>{s.type}</h3>
                      <button className="copy-button" onClick={() => copy(block, `s-${i}`)}>
                        {copiedKey === `s-${i}` ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    {s.note && <p className="output-intro" style={{ marginBottom: 12 }}>{s.note}</p>}
                    <pre>{block}</pre>
                  </StyledCodeBlock>
                );
              })}

            <StyledHowTo>
              <h4>How to add it to your site</h4>
              <ol>
                <li>Review the generated values against your real page and fix anything the AI missed.</li>
                <li>Copy the code and paste it inside the <code>&lt;head&gt;</code> of the page it describes.</li>
                <li>Validate it with the <a href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer">Google Rich Results Test</a> or the <a href="https://validator.schema.org/" target="_blank" rel="noopener noreferrer">Schema.org Validator</a>.</li>
                <li>Deploy, then request indexing in Google Search Console to speed things up.</li>
              </ol>
            </StyledHowTo>
          </StyledOutputCard>
        )}

        {recommendedWidgets.length > 0 && (
          <StyledWidgetsSection>
            <h2>Build better pages around your schema</h2>
            <p className="subtitle">
              Structured data tells search engines what your page is. These free widgets make the page better for people.
            </p>
            <StyledWidgetGrid>
              {recommendedWidgets.map((widget) => (
                <StyledWidgetCard key={widget._id} href={`/widgets/${widget.slug}`} target="_blank" rel="noopener noreferrer">
                  <div className="widget-header">
                    {widget.logo && <Image src={widget.logo} alt={`${widget.name} logo`} width={48} height={48} />}
                    <h3>{widget.name}</h3>
                  </div>
                  <p>{getWidgetDescription(widget.slug)}</p>
                  <span className="widget-cta">{getWidgetCTA(widget.slug)}</span>
                </StyledWidgetCard>
              ))}
            </StyledWidgetGrid>
          </StyledWidgetsSection>
        )}

        {relatedTools.length > 0 && (
          <StyledRelatedTools>
            <h2>Prefer to build it by hand?</h2>
            <p className="subtitle">Use a dedicated generator for full control over every field.</p>
            <StyledRelatedToolGrid>
              {relatedTools.map((tool) => (
                <StyledRelatedToolCard key={tool.slug} as={Link} href={`/free-tools/seo/${tool.slug}`}>
                  <span className="tool-icon">{TOOL_ICONS[tool.slug] || <HiOutlineSearch />}</span>
                  <h3>{tool.name}</h3>
                  <p>{tool.description}</p>
                  <span className="tool-cta">Use Tool &#8594;</span>
                </StyledRelatedToolCard>
              ))}
            </StyledRelatedToolGrid>
          </StyledRelatedTools>
        )}
      </StyledToolBody>
    </>
  );
};
